<template>
  <article
    :class="bemm('', [status])"
    :role="role"
    :aria-live="ariaLive"
    :style="`--notification-color: var(--color-${status})`"
  >
    <Icon :name="resolvedIcon" :class="bemm('icon')" />
    <div :class="bemm('content')">
      <p v-if="title" :class="bemm('title')">{{ title }}</p>
      <p :class="bemm('message')">
        <slot>{{ message }}</slot>
      </p>
    </div>
    <Button
      v-if="closable"
      :class="bemm('close')"
      :icon="Icons.CROSS_SMALL"
      variant="ghost"
      size="small"
      :aria-label="closeAriaLabel"
      @click="emit('close')"
    />
  </article>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useBemm } from 'bemm';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { IconNameOrString } from '../Icon';
import { Icons } from '../Icon';
import {
	NotificationStatus,
	type NotificationStatus as NotificationStatusType,
} from './Notification.model';

const props = withDefaults(
	defineProps<{
		status?: NotificationStatusType;
		title?: string;
		message?: string;
		icon?: IconNameOrString | null;
		closable?: boolean;
		closeAriaLabel?: string;
	}>(),
	{
		status: NotificationStatus.INFO,
		title: '',
		message: '',
		icon: null,
		closable: false,
		closeAriaLabel: 'Close notification',
	}
);
const emit = defineEmits<{
	(event: 'close'): void;
}>();

const bemm = useBemm('ar-notification', {
	return: 'string',
	includeBaseClass: true,
});

const statusIcons: Record<NotificationStatusType, IconNameOrString> = {
	[NotificationStatus.INFO]: Icons.INFO,
	[NotificationStatus.SUCCESS]: Icons.CHECK_CIRCLE,
	[NotificationStatus.WARNING]: Icons.EXCLAMATION,
	[NotificationStatus.ERROR]: Icons.CROSS,
};

const resolvedIcon = computed(
	() => props.icon || statusIcons[props.status] || Icons.INFO
);

const role = computed(() =>
	props.status === NotificationStatus.ERROR ? 'alert' : 'status'
);

const ariaLive = computed(() =>
	props.status === NotificationStatus.ERROR ? 'assertive' : 'polite'
);
</script>

<style lang="scss">
.ar-notification {
  --notification-color: var(--color-info);
  --notification-bg-color: color-mix(in srgb, var(--notification-color), transparent 90%);
  --notification-text-color: color-mix(
    in srgb,
    var(--notification-color),
    var(--color-foreground) 80%
  );

  display: flex;
  align-items: flex-start;
  gap: var(--space-s);
  padding: var(--space-s) calc(var(--space-m) + var(--space-l)) var(--space-s)
    var(--space-m);
  border: 1px solid var(--notification-color);
  border-radius: var(--border-radius);
  background: var(--notification-bg-color);
  color: var(--notification-text-color);
  position: relative;

  &--info {
    --notification-color: var(--color-info);
  }

  &--success {
    --notification-color: var(--color-success);
  }

  &--warning {
    --notification-color: var(--color-warning);
  }

  &--error {
    --notification-color: var(--color-error);
  }

  &__icon {
    color: var(--notification-color);
    font-size: var(--font-size-m);
    margin-top: calc(var(--space-xs) * 0.5);
    flex-shrink: 0;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: calc(var(--space-xs) * 0.5);
  }

  &__close.ar-button {
    position: absolute;
    top: 2px;
    right: 2px;
    opacity: 0.55;
    display: inline-flex;
    margin: 0;
    z-index: 1;
    transition: opacity 120ms ease, background-color 120ms ease;
  }

  &__close:hover {
    opacity: 0.85;
    background: color-mix(in srgb, var(--color-foreground), transparent 94%);
  }

  &__title {
    margin: 0;
    font-size: var(--font-size-s);
    font-weight: var(--font-weight-bold);
  }

  &__message {
    margin: 0;
    font-size: var(--font-size-s);
    line-height: 1.4;
  }
}
</style>
